/**
 * Simple localStorage cache with expiration support
 * Uses "stale-while-revalidate" pattern for instant loading
 */

const CACHE_PREFIX = 'oproadservice_cache_';
const DEFAULT_TTL = 5 * 60 * 1000; // 5 minutes (data considered "fresh")
const STALE_TTL = 24 * 60 * 60 * 1000; // 24 hours (data still usable but should refresh)

/**
 * Get cached data
 * @param {string} key - Cache key
 * @returns {{ data: any, isFresh: boolean, isStale: boolean } | null}
 */
export const getCache = (key) => {
  try {
    const cached = localStorage.getItem(CACHE_PREFIX + key);
    if (!cached) return null;
    
    const { data, timestamp } = JSON.parse(cached);
    const age = Date.now() - timestamp;
    
    // Data is too old, don't use it
    if (age > STALE_TTL) {
      localStorage.removeItem(CACHE_PREFIX + key);
      return null;
    }
    
    return {
      data,
      isFresh: age < DEFAULT_TTL,
      isStale: age >= DEFAULT_TTL
    };
  } catch (error) {
    console.error('Cache read error:', error);
    return null;
  }
};

/**
 * Set cached data
 * @param {string} key - Cache key
 * @param {any} data - Data to cache
 */
export const setCache = (key, data) => {
  try {
    const cacheEntry = {
      data,
      timestamp: Date.now()
    };
    localStorage.setItem(CACHE_PREFIX + key, JSON.stringify(cacheEntry));
  } catch (error) {
    console.error('Cache write error:', error);
    // If localStorage is full, clear old cache entries
    if (error.name === 'QuotaExceededError') {
      clearOldCache();
    }
  }
};

/**
 * Clear specific cache key
 * @param {string} key - Cache key to clear
 */
export const clearCache = (key) => {
  try {
    localStorage.removeItem(CACHE_PREFIX + key);
  } catch (error) {
    console.error('Cache clear error:', error);
  }
};

/**
 * Clear all app cache
 */
export const clearAllCache = () => {
  try {
    Object.keys(localStorage)
      .filter(key => key.startsWith(CACHE_PREFIX))
      .forEach(key => localStorage.removeItem(key));
  } catch (error) {
    console.error('Cache clear all error:', error);
  }
};

/**
 * Clear old cache entries (older than STALE_TTL)
 */
const clearOldCache = () => {
  try {
    Object.keys(localStorage)
      .filter(key => key.startsWith(CACHE_PREFIX))
      .forEach(key => {
        try {
          const cached = localStorage.getItem(key);
          if (cached) {
            const { timestamp } = JSON.parse(cached);
            if (Date.now() - timestamp > STALE_TTL) {
              localStorage.removeItem(key);
            }
          }
        } catch {
          localStorage.removeItem(key);
        }
      });
  } catch (error) {
    console.error('Cache cleanup error:', error);
  }
};

/**
 * Cache keys for different data types
 */
export const CACHE_KEYS = {
  DASHBOARD_STATS: 'dashboard_stats',
  INVOICES_PAGE: (page, status) => `invoices_p${page}_s${status}`,
  INVOICES_COUNT: (status) => `invoices_count_${status}`,
  CUSTOMERS: 'customers',
  VEHICLES: 'vehicles',
  SETTINGS: 'settings'
};
